import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #667eea 0%, #764ba2 100%);
  color: white;
  overflow-x: hidden;
`;

const Header = styled.header`
  position: relative;
  padding: 2rem;
  text-align: center;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-5px);
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  background: linear-gradient(to right, #ffffff, #f0f0f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const MainContent = styled.div`
  background: #f9fafb;
  color: #333;
  padding: 5rem 2rem;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const DownloadSection = styled.section`
  text-align: center;
  margin-bottom: 5rem;
`;

const DownloadTitle = styled.h2`
  font-size: 2.5rem;
  color: #4f46e5;
  margin-bottom: 2rem;
`;

const DownloadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const DownloadCard = styled(motion.a)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  text-decoration: none;
  color: #333;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const DownloadIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(to right, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const DownloadPlatform = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #4f46e5;
`;

const DownloadFormat = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
`;

const DownloadButton = styled.span`
  background: linear-gradient(to right, #667eea, #764ba2);
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
`;

const FeaturesSection = styled.section`
  margin-bottom: 5rem;
`;

const FeaturesTitle = styled.h2`
  font-size: 2.5rem;
  color: #4f46e5;
  text-align: center;
  margin-bottom: 3rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: linear-gradient(to right, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  color: #4f46e5;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
`;

const SpecSection = styled.section`
  background: linear-gradient(to right, #4f46e5, #7c3aed);
  color: white;
  padding: 4rem 2rem;
  border-radius: 24px;
  margin-bottom: 3rem;
`;

const SpecTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const SpecItem = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const SpecLabel = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
`;

const SpecValue = styled.p`
  font-size: 1.3rem;
  font-weight: 600;
`;

const InterviewGeniePage: React.FC = () => {
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.6 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      <PageContainer>
        <Header>
          <BackButton to="/">
            ← Back to Portfolio
          </BackButton>
          
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Interview Genie
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Real-time AI assistance for online interviews and conversations
          </HeroSubtitle>
        </Header>

        <MainContent>
          <ContentContainer>
            <DownloadSection>
              <DownloadTitle>Download Interview Genie</DownloadTitle>
              <p style={{ fontSize: '1.2rem', color: '#6b7280', marginBottom: '2rem' }}>
                Available for macOS and Windows. Choose your platform below.
              </p>
              
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <DownloadGrid>
                  <DownloadCard
                    href="https://github.com/ben8987/InterviewGenie-Releases/releases/download/v3.2.7/InterviewGenie_0.1.0_aarch64.dmg"
                    variants={cardVariants}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <DownloadIcon>🍎</DownloadIcon>
                    <DownloadPlatform>macOS</DownloadPlatform>
                    <DownloadFormat>Apple Silicon (M1/M2/M3)</DownloadFormat>
                    <DownloadButton>Download .dmg</DownloadButton>
                  </DownloadCard>

                  <DownloadCard
                    href="https://github.com/ben8987/InterviewGenie-Releases/releases/download/v3.2.7/InterviewGenie_0.1.0_x64-setup.exe"
                    variants={cardVariants}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <DownloadIcon>🪟</DownloadIcon>
                    <DownloadPlatform>Windows</DownloadPlatform>
                    <DownloadFormat>Setup Installer</DownloadFormat>
                    <DownloadButton>Download .exe</DownloadButton>
                  </DownloadCard>

                  <DownloadCard
                    href="https://github.com/ben8987/InterviewGenie-Releases/releases/download/v3.2.7/InterviewGenie_0.1.0_x64_en-US.msi"
                    variants={cardVariants}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <DownloadIcon>📦</DownloadIcon>
                    <DownloadPlatform>Windows</DownloadPlatform>
                    <DownloadFormat>MSI Package</DownloadFormat>
                    <DownloadButton>Download .msi</DownloadButton>
                  </DownloadCard>
                </DownloadGrid>
              </motion.div>
            </DownloadSection>

            <FeaturesSection>
              <FeaturesTitle>Key Features</FeaturesTitle>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <FeaturesGrid>
                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🎯</FeatureIcon>
                    <FeatureTitle>Real-time Hints & Answers</FeatureTitle>
                    <FeatureDescription>
                      Get intelligent, context-aware suggestions and answers based on the ongoing conversation to help you articulate your thoughts effectively.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🔊</FeatureIcon>
                    <FeatureTitle>System Audio Capture</FeatureTitle>
                    <FeatureDescription>
                      Listens to your computer's speaker output, analyzing the interviewer's questions and conversation context in real-time.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🤖</FeatureIcon>
                    <FeatureTitle>OpenAI Powered</FeatureTitle>
                    <FeatureDescription>
                      Utilizes OpenAI's advanced Realtime Conversation API for intelligent, contextual responses and suggestions.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>⚡</FeatureIcon>
                    <FeatureTitle>Low Latency</FeatureTitle>
                    <FeatureDescription>
                      Optimized for real-time performance with minimal delay between question detection and answer generation.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🎨</FeatureIcon>
                    <FeatureTitle>Minimalist UI</FeatureTitle>
                    <FeatureDescription>
                      Clean, distraction-free interface with easy start/stop controls and a clear display area for text hints.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🌐</FeatureIcon>
                    <FeatureTitle>Cross-Platform</FeatureTitle>
                    <FeatureDescription>
                      Works seamlessly on both macOS and Windows, providing consistent performance across different operating systems.
                    </FeatureDescription>
                  </FeatureCard>
                </FeaturesGrid>
              </motion.div>
            </FeaturesSection>

            <SpecSection>
              <SpecTitle>Technical Specifications</SpecTitle>
              <SpecGrid>
                <SpecItem>
                  <SpecLabel>Platform Support</SpecLabel>
                  <SpecValue>macOS & Windows</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Audio Processing</SpecLabel>
                  <SpecValue>Real-time System Audio</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>AI Engine</SpecLabel>
                  <SpecValue>OpenAI Realtime API</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Response Time</SpecLabel>
                  <SpecValue>&lt; 500ms</SpecValue>
                </SpecItem>
              </SpecGrid>
            </SpecSection>
          </ContentContainer>
        </MainContent>
      </PageContainer>
    </motion.div>
  );
};

export default InterviewGeniePage; 